import { DateTime } from 'luxon';

import { MIDNIGHT_CUTOFF_HOURS } from '@/data/constants';
import { TimeZone } from '@/data/models/time-zone.model';

type EffectiveDateRange = {
  effectiveStartDate?: string;
  effectiveEndDate?: string;
};

class DateUtils {
  public readonly WORK_TIME_ZONE = 'America/Los_Angeles';

  public getTimeZoneIana(timezones: TimeZone[], timeZoneId: number): string {
    const tz = timezones.find((t) => t.timeZoneID === timeZoneId);
    return tz?.ianaStandard || '';
  }

  // TODO: at some point we can get more sophisticated about what we set as min
  // times for each of the time controls in the reservation dialog. For now
  // we just set it to the current time on the West Coast since that is
  // what the CSA agents work in.
  public getDefaultMinTime(currentDate?: DateTime) {
    const today = DateTime.now().setZone(this.WORK_TIME_ZONE).endOf('day');
    if (currentDate && currentDate > today) {
      return undefined;
    }
    return DateTime.now().setZone(this.WORK_TIME_ZONE);
  }

  public getDefaultMaxTime({
    currentDate,
    maxDate,
  }: {
    currentDate?: DateTime;
    maxDate?: DateTime;
  }) {
    if (currentDate && maxDate && currentDate < maxDate.startOf('day')) {
      return undefined;
    }
    return maxDate;
  }

  public formatTime(dateTime: DateTime) {
    return dateTime ? dateTime.toFormat('h:mma') : '';
  }

  public formatDate(dateTime: DateTime) {
    return dateTime ? dateTime.toFormat('MM/dd/yyyy') : '';
  }

  public formatDateTime(dateTime: DateTime) {
    let dt = dateTime ? dateTime.toLocaleString(DateTime.DATETIME_SHORT) : '';
    // Remove space before AM/PM
    const idx = dt.lastIndexOf(' ');
    if (idx >= 0) {
      dt = dt.substring(0, idx) + dt.substring(idx + 1);
    }
    return dt;
  }

  public formatAppointmentTime(
    appointmentDateTime: string | null | undefined,
    timeZone: string,
  ) {
    if (appointmentDateTime) {
      return this.formatTime(
        DateTime.fromISO(appointmentDateTime, { zone: timeZone }),
      );
    }
    return '';
  }

  public formatAppointmentDate(
    appointmentDate: string | null | undefined,
    timeZone: string,
  ) {
    if (appointmentDate) {
      return this.formatDate(
        DateTime.fromISO(appointmentDate, {
          zone: timeZone,
        }),
      );
    }
    return '';
  }

  public formatPickupDateTime(
    pickupDateTime: string | null | undefined,
    timeZone: string,
  ) {
    if (pickupDateTime) {
      return this.formatDateTime(
        DateTime.fromISO(pickupDateTime, {
          zone: timeZone,
        }),
      );
    }
    return '';
  }

  public formatPickupTime(
    pickupDateTime: string | null | undefined,
    timeZone: string,
  ) {
    if (pickupDateTime) {
      return this.formatTime(
        DateTime.fromISO(pickupDateTime, {
          zone: timeZone,
        }),
      );
    }
    return '';
  }

  public shouldDefaultToPreviousDay(
    appointmentTime: DateTime,
    facilityTimeZone: string,
  ): boolean {
    const appointmentInFacilityTz = appointmentTime.setZone(facilityTimeZone);
    const appointmentHour = appointmentInFacilityTz.hour;
    return appointmentHour >= 0 && appointmentHour < MIDNIGHT_CUTOFF_HOURS;
  }

  public getDefaultPickupDate(
    appointmentTime: DateTime,
    facilityTimeZone: string,
  ): DateTime {
    if (this.shouldDefaultToPreviousDay(appointmentTime, facilityTimeZone)) {
      return appointmentTime.minus({ days: 1 }).startOf('day');
    }
    return appointmentTime.startOf('day');
  }

  public isReturnDateValid(
    returnDate: DateTime | null,
    pickupDate: DateTime | null,
  ): boolean {
    if (!returnDate || !pickupDate) return true;
    return returnDate.startOf('day') >= pickupDate.startOf('day');
  }

  public getIntersectingDateRange(
    firstDateRange: EffectiveDateRange,
    secondDateRange: EffectiveDateRange,
  ): Partial<EffectiveDateRange> {
    if (
      !firstDateRange.effectiveStartDate ||
      !secondDateRange.effectiveStartDate
    ) {
      return { effectiveStartDate: undefined, effectiveEndDate: undefined };
    }
    const { effectiveStartDate, effectiveEndDate } = {
      effectiveStartDate:
        (!firstDateRange.effectiveStartDate &&
          secondDateRange.effectiveStartDate) ||
        (!secondDateRange.effectiveStartDate &&
          firstDateRange.effectiveStartDate) ||
        (firstDateRange.effectiveStartDate! <
        secondDateRange.effectiveStartDate!
          ? secondDateRange.effectiveStartDate
          : firstDateRange.effectiveStartDate),
      effectiveEndDate:
        (!firstDateRange.effectiveEndDate &&
          secondDateRange.effectiveEndDate) ||
        (!secondDateRange.effectiveEndDate &&
          firstDateRange.effectiveEndDate) ||
        (firstDateRange.effectiveEndDate! < secondDateRange.effectiveEndDate!
          ? firstDateRange.effectiveEndDate
          : secondDateRange.effectiveEndDate),
    };
    if (
      effectiveStartDate &&
      effectiveEndDate &&
      effectiveStartDate > effectiveEndDate
    ) {
      return { effectiveStartDate: undefined, effectiveEndDate: undefined };
    }
    return { effectiveStartDate, effectiveEndDate };
  }
}

const dateUtils = new DateUtils();

export default dateUtils;
